<template>
  <el-card class="summary" shadow="never" v-loading="fetchingPerformanceRates">
    <template #header>
      <div class="card-header">
        <el-form inline size="small">
          <el-form-item label="统计时间">
            <el-date-picker v-model="startOn" type="month" value-format="yyyy-MM" />
          </el-form-item>
          <el-form-item label="业务员" v-if="roleKind !== 'salesman' && roleKind !== 'customer_service'">
            <el-select v-model="userId" placeholder="请选择" filterable clearable>
              <el-option v-for="user in users" :key="user.id" :label="user.screen_name" :value="user.id" />
            </el-select>
          </el-form-item>
        </el-form>
        <el-button type="primary" size="small" @click="exportToExcel">导出</el-button>
      </div>
    </template>

    <div class="box-main">
      <el-card shadow="never">
        <ECharts
          ref="chart"
          :options="chartOption"
          class="chart"
          autoresize
        />
      </el-card>

      <el-card shadow="never">
        <el-table :data="tableData" border :show-header="false">
          <template v-if="performanceGoalTargets.includes('finish_target')">
            <el-table-column prop="name" label="" min-width="80" />
            <el-table-column prop="value1" label="" min-width="60" align="center" />
          </template>
          <template v-if="performanceGoalTargets.includes('sales_target')">
            <el-table-column prop="name2" label="" min-width="80" />
            <el-table-column prop="value2" label="" min-width="60" align="center" />
          </template>
          <template v-if="performanceGoalTargets.includes('shipping_target')">
            <el-table-column prop="name3" label="" min-width="80" />
            <el-table-column prop="value3" label="" min-width="60" align="center" />
          </template>
        </el-table>
      </el-card>
    </div>

    <br>
    <Details :details="performanceRates.details" />
  </el-card>
</template>

<script>
import { Vue, Component, Prop, Watch, Mixins } from 'vue-property-decorator';
import ECharts from 'vue-echarts';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/title';
import 'echarts/lib/chart/bar';
import moment from 'moment';
import Utils from '@/components/utils.vue';
import * as XLSX from 'xlsx';
import Details from './details.vue';
import _ from 'lodash';

@Component({
  components: {
    ECharts,
    Details,
  },
  filters: {
    toLocaleString(value) {
      return Number(value).toLocaleString('zh-CN');
    }
  }
})
export default class Summary extends Mixins(Vue, Utils) {
  @Prop({ type: Array, required: true }) users;

  startOn = moment().format('YYYY-MM');

  userId = '';
  performanceRates = {
    details: [],
    summary: {
      finish_target: '0.0',
      finish_amount: '0.0',
      sales_target: '0.0',
      sales_amount: '0.0',
    }
  }

  get currentUser() {
    return this.users.find(user => user.id === this.userId);
  }

  get rankUserDetail() {
    let userId;
    if (['salesman', 'customer_service'].includes(this.roleKind)) {
      userId = this.$root.user.id;
    } else {
      userId = this.userId;
    }
    if (!userId) return null;
    return this.performanceRates.details.find(item => item.user_id === userId);
  }

  get performanceRatesSummary() {
    let summry = this.performanceRates.summary;
    if (this.userId) {
      summry = this.performanceRates.details.find(item => item.user_id === this.userId) || {};
    }
    const finishTarget = Number(summry.finish_target) || 0;
    const finishAmount = Number(summry.finish_amount) || 0;
    const salesTarget = Number(summry.sales_target) || 0;
    const salesAmount = Number(summry.sales_amount) || 0;
    const shippingTarget = Number(summry.shipping_target) || 0;
    const shippingAmount = Number(summry.shipping_amount) || 0;
    return {
      finish_target: finishTarget,
      finish_amount: finishAmount,
      sales_target: salesTarget,
      sales_amount: salesAmount,
      shipping_target: shippingTarget,
      shipping_amount: shippingAmount,
      sales_diff: salesTarget - salesAmount,
      finish_diff: finishTarget - finishAmount,
      shipping_diff: shippingTarget - shippingAmount,
      sales_percent: this.calcFinishPercent(salesAmount, salesTarget),
      finish_percent: this.calcFinishPercent(finishAmount, finishTarget),
      shipping_percent: this.calcFinishPercent(shippingAmount, shippingTarget),
    };
  }

  get tableData() {
    return [
      {
        name: '完结目标',
        value1: this.performanceRatesSummary.finish_target.toLocaleString('zh-CN'),
        name2: '开单目标',
        value2: this.performanceRatesSummary.sales_target.toLocaleString('zh-CN'),
        name3: '出货目标',
        value3: this.performanceRatesSummary.shipping_target.toLocaleString('zh-CN')
      },
      {
        name: '实际完结金额',
        value1: this.performanceRatesSummary.finish_amount.toLocaleString('zh-CN'),
        name2: '实际开单金额',
        value2: this.performanceRatesSummary.sales_amount.toLocaleString('zh-CN'),
        name3: '实际出货金额',
        value3: this.performanceRatesSummary.shipping_amount.toLocaleString('zh-CN')
      },
      {
        name: '完成率',
        value1: this.performanceRatesSummary.finish_percent,
        name2: '完成率',
        value2: this.performanceRatesSummary.sales_percent,
        name3: '完成率',
        value3: this.performanceRatesSummary.shipping_percent
      },
      {
        name: '目标差额',
        value1: this.performanceRatesSummary.finish_diff > 0 ? this.performanceRatesSummary.finish_diff.toLocaleString('zh-CN') : '已达成',
        name2: '目标差额',
        value2: this.performanceRatesSummary.sales_diff > 0 ? this.performanceRatesSummary.sales_diff.toLocaleString('zh-CN') : '已达成',
        name3: '目标差额',
        value3: this.performanceRatesSummary.shipping_diff > 0 ? this.performanceRatesSummary.shipping_diff.toLocaleString('zh-CN') : '已达成'
      },
      this.rankUserDetail && {
        name: '完结排名',
        value1: this.rankUserDetail.rank_by_finish_amount,
        name2: '开单排名',
        value2: this.rankUserDetail.rank_by_sales_amount,
        name3: '出货排名',
        value3: this.rankUserDetail.rank_by_shipping_amount
      }
    ].filter(Boolean);
  }

  fetchingPerformanceRates = false;
  request = null;

  calcFinishPercent(finishAmount, finishTarget) {
    return finishTarget ? (
      finishAmount ? `${((finishAmount / finishTarget) * 100).toFixed(2)}%` : '0%'
    ) : '-';
  }

  beforeDestroy() {
    if (this.request && this.request.engine) {
      this.request.engine.abort();
    }
  }

  @Watch('startOn', { immediate: true })
  async fetchPerformancesRates() {
    if (!this.startOn) return;
    this.fetchingPerformanceRates = true;
    try {
      if (this.request && this.request.engine) {
        this.request.engine.abort();
      }
      const endOn = moment(`${this.startOn}-01`).endOf('month').format('YYYY-MM-DD');
      this.request = this.$request.get('/reports/sales_follows/performance_rates', {
        start_on: `${this.startOn}-01`,
        end_on: endOn,
      }, { timeout: 3 * 60 * 1000, loading: false });
      const data = await this.request;
      this.performanceRates = data;
    } finally {
      this.fetchingPerformanceRates = false;
    }
  }

  get chartOption() {
    const data = [
      {
        name: '开单',
        if: this.performanceGoalTargets.includes('sales_target'),
        target: this.performanceRatesSummary.sales_target,
        amount: {
          value: this.performanceRatesSummary.sales_amount,
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: () => {
              return [
                `完成率: ${this.performanceRatesSummary.sales_percent}`,
                this.performanceRatesSummary.sales_diff > 0 ? `还差: ${this.performanceRatesSummary.sales_diff}` : '已达成'
              ].filter(Boolean).join('\n');
            }
          }
        },
      },
      {
        name: '完结',
        if: this.performanceGoalTargets.includes('finish_target'),
        target: this.performanceRatesSummary.finish_target,
        amount: {
          value: this.performanceRatesSummary.finish_amount,
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: () => {
              return [
                `完成率: ${this.performanceRatesSummary.finish_percent}`,
                this.performanceRatesSummary.finish_diff > 0 ? `还差: ${this.performanceRatesSummary.finish_diff}` : '已达成'
              ].filter(Boolean).join('\n');
            }
          }
        },
      },
      {
        name: '出货',
        if: this.performanceGoalTargets.includes('shipping_target'),
        target: this.performanceRatesSummary.shipping_target,
        amount: {
          value: this.performanceRatesSummary.shipping_amount,
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: () => {
              return [
                `完成率: ${this.performanceRatesSummary.shipping_percent}`,
                this.performanceRatesSummary.shipping_diff > 0 ? `还差: ${this.performanceRatesSummary.shipping_diff}` : '已达成'
              ].filter(Boolean).join('\n');
            }
          }
        }
      },
    ].filter(item => item.if);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        }
      },
      legend: {
        data: ['目标', '实际金额']
      },
      grid: {
        left: '3%',
        right: '15%',
        bottom: '10%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: '金额',
        axisLabel: {
          formatter: (value) => {
            return (value / 10000).toFixed(1) + 'w';
          }
        }
      },
      yAxis: {
        type: 'category',
        data: data.map(item => item.name)
      },
      series: [
        {
          name: '目标',
          type: 'bar',
          barWidth: 30,
          itemStyle: {
            color: '#101ebf'
          },
          data: data.map(item => item.target)
        },
        {
          name: '实际金额',
          type: 'bar',
          barWidth: 30,
          itemStyle: {
            color: '#67c23a'
          },
          data: data.map(item => item.amount)
        }
      ]
    };
  }

  exportToExcel() {
    const visibleKeysMap = {
      finish_target: ['业务员', '完结目标', '实际完结金额', '完结完成率', '完结差额'],
      sales_target: ['业务员', '开单目标', '实际开单金额', '开单完成率', '开单差额'],
      shipping_target: ['业务员', '出货目标', '实际出货金额', '出货完成率', '出货差额'],
    }

    const visibleKeys = _.uniq(this.performanceGoalTargets.flatMap(key => visibleKeysMap[key]));

    const exportData = this.performanceRates.details.map(item => {
      const salesDiff = Number(item.sales_target) - Number(item.sales_amount);
      const finishDiff = Number(item.finish_target) - Number(item.finish_amount);
      const shippingDiff = Number(item.shipping_target) - Number(item.shipping_amount);
      return _.pick({
        '业务员': item.screen_name,
        '完结目标': Number(item.finish_target),
        '实际完结金额': Number(item.finish_amount),
        '开单目标': Number(item.sales_target),
        '实际开单金额': Number(item.sales_amount),
        '出货目标': Number(item.shipping_target),
        '实际出货金额': Number(item.shipping_amount),
        '完结完成率': Number(item.finish_target) === 0 ? '-' :
          `${((Number(item.finish_amount) / Number(item.finish_target)) * 100).toFixed(2)}%`,
        '开单完成率': Number(item.sales_target) === 0 ? '-' :
          `${((Number(item.sales_amount) / Number(item.sales_target)) * 100).toFixed(2)}%`,
        '出货完成率': Number(item.shipping_target) === 0 ? '-' :
        `${((Number(item.shipping_amount) / Number(item.shipping_target)) * 100).toFixed(2)}%`,
        '开单差额': salesDiff > 0 ? salesDiff : '已达成',
        '完结差额': finishDiff > 0 ? finishDiff : '已达成',
        '出货差额': shippingDiff > 0 ? shippingDiff : '已达成',
      }, visibleKeys);
    });

    // 添加合计行
    exportData.push(_.pick({
      '业务员': '合计',
      '完结目标': this.performanceRatesSummary.finish_target,
      '实际完结金额': this.performanceRatesSummary.finish_amount,
      '开单目标': this.performanceRatesSummary.sales_target,
      '实际开单金额': this.performanceRatesSummary.sales_amount,
      '出货目标': this.performanceRatesSummary.shipping_target,
      '实际出货金额': this.performanceRatesSummary.shipping_amount,
      '完结完成率': this.performanceRatesSummary.finish_percent,
      '开单完成率': this.performanceRatesSummary.sales_percent,
      '出货完成率': this.performanceRatesSummary.shipping_percent,
      '开单差额': this.performanceRatesSummary.sales_diff > 0 ? this.performanceRatesSummary.sales_diff : '已达成',
      '完结差额': this.performanceRatesSummary.finish_diff > 0 ? this.performanceRatesSummary.finish_diff : '已达成',
      '出货差额': this.performanceRatesSummary.shipping_diff > 0 ? this.performanceRatesSummary.shipping_diff : '已达成',
    }, visibleKeys));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, '业绩目标统计');

    // 生成文件名
    const fileName = `业绩目标统计_${this.startOn}.xlsx`;

    // 导出文件
    XLSX.writeFile(wb, fileName);
  }
}
</script>

<style lang="scss" scoped>
.summary {
  flex: 1;
  display: flex;
  flex-direction: column;

  /deep/.el-card__header {
    flex-shrink: 0;
    padding-top: 10px;
    padding-bottom: 10px;
  }

  /deep/.el-card__body {
    flex: 1;
    overflow: auto;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .el-form {
      flex: 1;
    }

    .el-button {
      margin-left: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
    }
  }

  .box-main {
    display: flex;
    gap: 20px;
    flex-wrap: wrap;
  }

  .chart {
    width: 100%;
    height: 400px;
  }

  .el-card {
    flex: 1;
  }
}

.rank-row {
  line-height: 1.6;

  .rank-value {
    font-weight: bold;
  }
}
</style>
